import React, { createContext, JSX, useContext, useState } from 'react';
import { User } from '../types';
import * as Sentry from '@sentry/react';
import { logger } from '../util/Logger';
import { changeLanguage } from 'i18next';
import { getLanguage, language } from 'util/Language';
import { getLocale } from 'util/Locales';
import { registerLocale, setDefaultLocale } from 'react-datepicker';

interface Props {
  user?: User | undefined;
  children: React.ReactNode;
}

export type UserContextType = {
  user: User | undefined;
  setUser: (user: User | undefined) => void;

  superAdminMode: boolean;
  setSuperAdminMode: (enabled: boolean) => void;

  lang: language | undefined;
};

const UserContext = createContext<UserContextType>({
  user: undefined,
  setUser: () => logger.warn('no User provider'),
  superAdminMode: false,
  setSuperAdminMode: () => logger.warn('no User provider'),
  lang: undefined,
});

export function useUserContext(): UserContextType {
  return useContext(UserContext);
}

export function UserProvider({ children }: Props): JSX.Element {
  const [user, setUser] = useState<User>();
  const [superAdminMode, setSuperAdminMode] = useState<boolean>(false);

  const setLocaleForLanguage = async (lang: language) => {
    const locale = await getLocale(lang.locale);

    if (!locale) {
      logger.warn(`No locale found for language "${lang.label}" (${lang.locale})`);
      return;
    }

    registerLocale(lang.locale, locale);
    setDefaultLocale(lang.locale);
    logger.info(`Set locale to "${lang.locale}" for language "${lang.label}"`);
  };

  // get the language object for the current user
  const lang = user ? getLanguage(user.lang) : undefined;

  // Wrap setUser so language and locale are applied when user changes
  const setUserWithLocale = (newUser: User | undefined) => {
    // avoid unnecessary re-renders when just the object reference changes
    if (JSON.stringify(newUser) === JSON.stringify(user)) return;

    setUser(newUser);

    if (newUser) {
      Sentry.setUser({
        id: newUser.id,
        email: newUser.email,
        username: newUser.firstName + ' ' + newUser.lastName,
      });
      changeLanguage(newUser.lang);
      const langObj = getLanguage(newUser.lang);
      if (langObj) {
        setLocaleForLanguage(langObj);
      } else {
        logger.warn(`No language found for value ${newUser.lang}`);
      }
    } else {
      Sentry.setUser(null);
    }
  };

  return (
    <UserContext.Provider value={{ user, setUser: setUserWithLocale, superAdminMode, setSuperAdminMode, lang }}>
      {children}
    </UserContext.Provider>
  );
}
